import { AtSign } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useChatMentions } from '../hooks/useChatMentions';
import { chatHtmlToPlainText } from '../utils';
import type { ChatMentionFeedItemResponse } from '@/lib/api';

interface MentionsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Open the mentioning message's channel (jumps to the conversation). */
  onSelectMention: (item: ChatMentionFeedItemResponse) => void;
}

/** Compact relative time: 45s → "now", "3m", "2h", "5d", else a short date. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return 'now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function channelLabel(item: ChatMentionFeedItemResponse): string {
  if (item.channelType === 'direct') return 'Direct message';
  return item.channelName ? `#${item.channelName}` : 'Channel';
}

/**
 * The "Mentions" inbox — a cross-channel list of recent messages that @mention
 * the current user. Clicking a row opens that conversation. Read-only feed (v1).
 */
export function MentionsPanel({ open, onOpenChange, onSelectMention }: MentionsPanelProps) {
  const { mentions, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useChatMentions(open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <AtSign className="h-4 w-4 text-primary" />
          <DialogTitle className="text-base font-semibold">Mentions</DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          Recent messages that mention you across all your channels.
        </DialogDescription>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-10">Loading mentions…</p>
          ) : isError ? (
            <p className="text-sm text-destructive text-center py-10">
              Couldn&apos;t load your mentions. Try again.
            </p>
          ) : mentions.length === 0 ? (
            <div className="text-center py-10 px-6">
              <AtSign className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No mentions yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                When someone @mentions you, it shows up here.
              </p>
            </div>
          ) : (
            <ul className="py-1">
              {mentions.map((item) => (
                <li key={item.messageId}>
                  <DialogClose asChild>
                    <button
                      type="button"
                      onClick={() => onSelectMention(item)}
                      className="w-full text-left px-4 py-2.5 hover:bg-muted/60 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium truncate">{item.senderName}</span>
                        <span className="text-[11px] text-muted-foreground flex-shrink-0 tabular-nums">
                          {relativeTime(item.createdAt)}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {channelLabel(item)}
                      </p>
                      <p className="text-[13px] text-foreground/80 truncate mt-0.5">
                        {chatHtmlToPlainText(item.content)}
                      </p>
                    </button>
                  </DialogClose>
                </li>
              ))}
            </ul>
          )}
          {hasNextPage && (
            <div className="p-2 border-t border-border">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full py-2 text-sm text-primary hover:bg-muted/60 rounded-lg transition-colors disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
